import { COLOR_HANDLE } from '../../constants';
import { screenPx } from '../../helpers/handleScale';
import {
  EDGE_STRIP_THICKNESS_PX,
  HANDLE_HIT_RADIUS_PX,
  ROTATE_HANDLE_SIZE_PX,
} from '../../helpers/handleTokens';
import { makeResizeCursorUrl } from '../../helpers/utility';
import { HandleHitArea, SquareHandle } from './handles/Handle';

interface Props {
  bbox: { x: number; y: number; width: number; height: number };
  // A persisted Group's own rotate — the whole handle set tilts with it.
  // Ad-hoc multiselect has no persisted rotation, so this stays 0 there.
  rotate?: number;
  scale: number;
}

export const MultiSelectBoundingBox = ({ bbox, rotate = 0, scale }: Props) => {
  const { x, y, width, height } = bbox;
  const hitR = screenPx(HANDLE_HIT_RADIUS_PX, scale);
  const handleSize = screenPx(ROTATE_HANDLE_SIZE_PX, scale);
  const tlBrCursor = makeResizeCursorUrl(45 + rotate);
  const trBlCursor = makeResizeCursorUrl(135 + rotate);
  const tbCursor = makeResizeCursorUrl(90 + rotate);
  const lrCursor = makeResizeCursorUrl(rotate);
  const stripThickness = screenPx(EDGE_STRIP_THICKNESS_PX, scale);
  const half = stripThickness / 2;
  const strokeWidth = screenPx(1, scale);
  const rotateX = x + width / 2 - handleSize / 2;
  const rotateY = y - handleSize * 1.5;

  const corners = [
    { id: 'multiselect-resize-top-left', cx: x, cy: y, cursor: tlBrCursor },
    {
      id: 'multiselect-resize-top-right',
      cx: x + width,
      cy: y,
      cursor: trBlCursor,
    },
    {
      id: 'multiselect-resize-bottom-right',
      cx: x + width,
      cy: y + height,
      cursor: tlBrCursor,
    },
    {
      id: 'multiselect-resize-bottom-left',
      cx: x,
      cy: y + height,
      cursor: trBlCursor,
    },
  ] as const;

  const pivotCx = x + width / 2;
  const pivotCy = y + height / 2;

  return (
    <g
      transform={
        rotate ? `rotate(${rotate}, ${pivotCx}, ${pivotCy})` : undefined
      }
    >
      {/* Invisible body — lets drag-to-move start from anywhere inside the
          bbox, not just on a member shape's own edge. Drawn first (under
          the edge strips/corners/rotate handle below) so those still win
          the hit-test near the border. */}
      <rect
        id="multiselect-body"
        x={x}
        y={y}
        width={width}
        height={height}
        fill="transparent"
        style={{ cursor: 'move' }}
        pointerEvents="all"
      />

      {/* Bounding box outline */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="none"
        stroke={COLOR_HANDLE}
        strokeWidth={strokeWidth}
        style={{ pointerEvents: 'none' }}
      />

      {/* Rotate handle */}
      <svg
        id="multiselect-rotate"
        className="rotating"
        x={rotateX}
        y={rotateY}
        width={handleSize}
        height={handleSize}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          id="multiselect-rotate"
          cx="12"
          cy="12"
          r="12"
          fill="var(--active-blue)"
        />
        <g id="multiselect-rotate" clipPath="url(#ms-clip)">
          <path
            d="M6 12.4405C6 15.7053 8.62681 18.345 11.8837 18.345C15.1373 18.345 17.7609 15.7053 17.7609 12.4405C17.7609 9.17247 15.1826 6.5749 11.9354 6.5749C11.7027 6.5749 11.4798 6.59757 11.2795 6.6332L12.5654 5.3668C12.7076 5.22105 12.7916 5.05263 12.7916 4.82591C12.7916 4.36599 12.4362 4 11.9903 4C11.7609 4 11.5735 4.07773 11.4249 4.23644L8.78837 6.92793C8.61712 7.09312 8.52988 7.31012 8.52988 7.53037C8.52988 7.76356 8.61389 7.96437 8.78837 8.13603L11.4249 10.8145C11.5735 10.9635 11.7609 11.0381 11.9871 11.0381C12.4362 11.0381 12.7916 10.685 12.7916 10.2283C12.7916 10.0016 12.7076 9.8332 12.5622 9.68745L11.1115 8.24615C11.3506 8.20081 11.6317 8.17814 11.9354 8.17814C14.2649 8.17814 16.0937 10.0502 16.0937 12.4405C16.0937 14.7822 14.2197 16.6736 11.8837 16.6736C9.54442 16.6736 7.66074 14.7822 7.66074 12.4405C7.66074 11.9352 7.32795 11.5854 6.84329 11.5854C6.35218 11.5854 6 11.9352 6 12.4405Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="ms-clip">
            <rect
              width="12"
              height="16"
              fill="white"
              transform="translate(6 4)"
            />
          </clipPath>
        </defs>
      </svg>

      {/* Edge strips — rendered before corners so corners sit on top */}
      <rect
        id="multiselect-resize-top"
        x={x}
        y={y - half}
        width={width}
        height={stripThickness}
        fill="transparent"
        style={{ cursor: tbCursor }}
        pointerEvents="all"
      />
      <rect
        id="multiselect-resize-bottom"
        x={x}
        y={y + height - half}
        width={width}
        height={stripThickness}
        fill="transparent"
        style={{ cursor: tbCursor }}
        pointerEvents="all"
      />
      <rect
        id="multiselect-resize-left"
        x={x - half}
        y={y}
        width={stripThickness}
        height={height}
        fill="transparent"
        style={{ cursor: lrCursor }}
        pointerEvents="all"
      />
      <rect
        id="multiselect-resize-right"
        x={x + width - half}
        y={y}
        width={stripThickness}
        height={height}
        fill="transparent"
        style={{ cursor: lrCursor }}
        pointerEvents="all"
      />

      {/* Corner handles — rendered last so they sit on top of edge strips */}
      {corners.map(({ id, cx, cy, cursor }) => (
        <g key={id}>
          {/* The transparent circle carries the id and the cursor; the visible square
              is inert so the larger hit target always wins. */}
          <HandleHitArea
            id={id}
            cx={cx}
            cy={cy}
            radius={hitR}
            cursor={cursor}
          />
          <SquareHandle cx={cx} cy={cy} scale={scale} interactive={false} />
        </g>
      ))}
    </g>
  );
};
